import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BRAND_LOGO_URL } from "@/data/landing";

/**
 * Generic legal page shell — friendly mobile-app template style.
 */
export default function LegalLayout({ title, lastUpdated, kicker = "Legal", children }) {
  const { t } = useI18n();
  return (
    <main
      data-testid="legal-page"
      className="relative min-h-screen bg-stone-50 dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50"
    >
      <header className="border-b border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-emerald-800 dark:hover:text-emerald-400 transition"
          >
            <ArrowLeft className="h-4 w-4" /> {t("legal.back")}
          </Link>
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-xl overflow-hidden bg-neutral-950 ring-1 ring-neutral-200 dark:ring-neutral-800">
              <img
                src={BRAND_LOGO_URL}
                alt={t("brand.name") + " logo"}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <span className="font-display font-bold tracking-tight">
              {t("brand.name")}
            </span>
          </div>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-5 sm:px-8 py-16 sm:py-24">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-800 dark:text-emerald-400">
          {kicker}
        </div>
        <h1 className="mt-3 font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight">
          {title}
        </h1>
        {lastUpdated && (
          <div className="mt-3 text-sm text-neutral-500">
            {t("legal.lastUpdated")} · {lastUpdated}
          </div>
        )}

        <div
          data-testid="legal-content"
          className="prose mt-10 max-w-none text-neutral-700 dark:text-neutral-300 leading-relaxed [&_h2]:font-display [&_h2]:font-semibold [&_h2]:text-2xl [&_h2]:mt-12 [&_h2]:mb-3 [&_h2]:text-neutral-900 dark:[&_h2]:text-neutral-50 [&_h3]:font-display [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:mt-8 [&_h3]:mb-2 [&_h3]:text-neutral-900 dark:[&_h3]:text-neutral-50 [&_p]:mt-4 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul>li]:mt-1.5 [&_a]:text-emerald-800 dark:[&_a]:text-emerald-400 [&_a]:underline [&_a]:underline-offset-2"
        >
          {children}
        </div>
      </article>
    </main>
  );
}
